import React, { useCallback } from 'react';
import { Button, Icon, Modal } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../Localization';
import { Report, useReports, ExportFormat } from '../../../Contexts/ReportsContext';
import { ReportFilterConfig } from '@aidkitorg/types/lib/admin-panel/filters';

type ReportExportModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  report: Report | null;
  viewFilters?: ReportFilterConfig;
}

export const ReportExportModal: React.FC<ReportExportModalProps> = ({
  open,
  setOpen,
  report,
  viewFilters
}) => {
  const L = useLocalizedStrings();
  const { initiateReportExport, isExporting } = useReports();

  const reportId = report?.id;
  const currentlyExporting = reportId ? isExporting(reportId) : false;

  const handleExport = useCallback(async (format: ExportFormat) => {
    if (!reportId) return;

    // Close the modal immediately
    setOpen(false);

    // Initiate the export - the page will handle toast notifications
    try {
      await initiateReportExport({
        reportId,
        format,
        viewFilters
      });
    } catch (error) {
      console.error('Failed to initiate export:', error);
    }
  }, [reportId, viewFilters, initiateReportExport, setOpen]);

  const handleExportCSV = () => handleExport('csv');
  const handleExportXLSX = () => handleExport('xlsx');

  return (
    <Modal
      title={L.admin_panel.reports.export_report}
      open={open}
      onClose={() => setOpen(false)}
    >
      <div className="pb-4">
        {L.admin_panel.reports.export_reports_desc}
      </div>

      {/* Export format buttons */}
      <div className="flex flex-col gap-y-2">
        <Button
          onClick={handleExportCSV}
          variant="secondary"
          className="!justify-start"
          isDisabled={currentlyExporting}
        >
          <div className="flex gap-4 items-center py-2">
            <Icon name="ArrowDownTrayIcon" role='decorative'/>
            <div className="!text-left">
              <div>{L.admin_panel.payments.csv_format}</div>
              <div className="font-light">
                {L.admin_panel.payments.csv_format_desc}
              </div>
            </div>
          </div>
        </Button>
        <Button
          onClick={handleExportXLSX}
          variant="secondary"
          className="!justify-start"
          isDisabled={currentlyExporting}
        >
          <div className="flex gap-4 items-center py-2">
            <Icon name="TableCellsIcon" role='decorative'/>
            <div className="!text-left">
              <div>{L.admin_panel.payments.xlsx_format}</div>
              <div className="font-light">
                {L.admin_panel.payments.xlsx_format_desc}
              </div>
            </div>
          </div>
        </Button>
      </div>
    </Modal>
  );
};

export default ReportExportModal;
